import type { DataImport, Vendor } from '@cruz-com/lookup'
import { NoSymbolIcon } from '@heroicons/react/24/outline'
import _ from 'lodash'
import moment from 'moment'
import { twMerge } from 'tailwind-merge'
import { CardBody, CardHeader, CardItem, CardNumberItem, Link, NoSsr } from '@/components'
import { DateTimeFormat } from '@/settings'

export default function LocationCard(d: Vendor.Location & DataImport.Location) {
  const {
    _id,
    name,
    dealer_id,
    source_location_id,
    address1,
    city,
    state_abbr,
    zip_code,
    phone,
    lead,
    statistic,
    date_update,
    date_create,
    date_update_inventory,
    place,
    is_place_found,
    verify,
    view_count,
    vehicle_view_count,
    phone_click_count,
    show_default_profile,
    is_active,
  } = d || {}

  const { is_name_matched, is_address_number_matched, is_phone_matched } = verify || {}

  const MatchResult = () => {
    if (!is_place_found || !place?.display_name) return null
    const x = []
    if (!is_name_matched) x.push('Name')
    if (!is_address_number_matched) x.push('Address')
    if (!is_phone_matched) x.push('Phone')
    if (x.length > 0) {
      return (
        <div>
          Not Matched: <span className='font-medium text-red-800 dark:text-red-600'>{_.join(x, ', ')}</span>
        </div>
      )
    }
    return null
  }

  function PlaceStatus() {
    if (place?.display_name) {
      return <div className='font-medium'>{place.display_name}</div>
    }
    if (place?.id && !place?.full_address) {
      return (
        <div className='font-medium flex'>
          Place ID: <span>{place.id}</span>
        </div>
      )
    }
    if (!is_active) {
      return <div className='font-medium'>Inactive, No Place Data</div>
    }
    if (!place) {
      return <div className='text-yellow-600 font-medium'>Waiting to get Place ID</div>
    }
    if (place && !place.id) {
      return <div className='text-red-700 font-medium'>Place ID not found</div>
    }
  }

  return (
    <>
      <Link href={`/carsforsale-import/location?id=${_id}`}>
        <CardHeader className={twMerge(!d.is_active && 'bg-gray-200 dark:bg-gray-700')}>
          <div className='truncate flex items-center gap-2 font-semibold'>
            {!d.is_active && <NoSymbolIcon className='size-5 text-red-700' title='Cancelled' />}
            {d?.is_ignored && <div className='font-semibold text-xs rounded bg-red-800 text-white px-2 py-1'>Ignored</div>}
            <div className='truncate'>{name}</div>
          </div>
        </CardHeader>
      </Link>
      <CardBody>
        <CardItem label='Dealer ID' value={dealer_id || '-'} />
        <CardItem label='Source Location ID' value={source_location_id || '-'} />
        <CardItem
          label='Date Created'
          value={
            <NoSsr>
              <div>
                <span className='font-medium ml-1'>{moment(date_create).fromNow()}</span>
                <span className='font-light text-xs ml-1'>({moment(date_create).format(DateTimeFormat)})</span>
              </div>
            </NoSsr>
          }
        />
        <CardItem
          label='Date Updated'
          value={
            date_update ? (
              <NoSsr>
                <div>
                  <span className='font-medium ml-1'>{moment(date_update).fromNow()}</span>
                  <span className='font-light text-xs ml-1'>({moment(date_update).format(DateTimeFormat)})</span>
                </div>
              </NoSsr>
            ) : (
              '-'
            )
          }
        />
        <CardItem label='Date Inventory Updated' value={date_update_inventory ? moment(date_update_inventory).fromNow() : '-'} />
        <CardItem label='New Makes' value={_.isEmpty(statistic?.new_makes) ? '-' : _.join(statistic?.new_makes, ',')} />
        <div className='grid grid-cols-5 divide-x'>
          <CardNumberItem label='Active Vehicles' value={statistic?.total || 0} />
          <CardNumberItem label='Vehicle Views' value={vehicle_view_count || 0} />
          <CardNumberItem label='Location Views' value={view_count || 0} />
          <CardNumberItem label='Phone Clicks' value={phone_click_count || 0} />
          <CardNumberItem label='Leads' value={lead?.total || 0} />
        </div>
        <div className='py-2 font-light text-sm/6'>
          {address1}
          <br />
          {city}, {state_abbr} {zip_code}
          <br />
          P: {phone || '-'}
          {show_default_profile && <span className='ml-2 font-medium text-red-800 dark:text-red-500'>(showing this number)</span>}
        </div>
        <div className='pt-2 font-light'>
          <div>Google Place:</div>
          {PlaceStatus()}
          {place?.full_address && <div className='truncate'>{place?.full_address || '-'}</div>}
          {place?.phone && <div>P: {place.phone}</div>}
          {place?.need_refresh && <div className='text-green-500'>In Refresh Queue</div>}
          <MatchResult />
          {place?.date_update && (
            <div>
              <NoSsr>
                <span className='font-light text-sm'>Updated: </span>
                <span className='font-medium ml-1'>{moment(place.date_update).fromNow()}</span>
                <span className='font-light text-xs ml-1'>({moment(place.date_update).format(DateTimeFormat)})</span>
              </NoSsr>
            </div>
          )}
        </div>
      </CardBody>
    </>
  )
}
